<template>
  <div class="offer-no-analytics-details">
    <div class="offers-no-analytics-header">
      <div class="custom-card-header">
        <span>{{ $t('noAnalytics') }}</span>
      </div>

      <div class="search-row">
        <DxTextBox
          v-model="searchText"
          :placeholder="$t('search')"
          mode="search"
          styling-mode="outlined"
          width="100%"
          value-change-event="input"
        />
      </div>
    </div>

    <div v-if="filteredNoAnalyticsRows.length">
      <div class="table-header offers-no-analytics-table-header">
        <div></div>
        <div>{{ $t('article') }}</div>
        <div>{{ $t('description') }}</div>
        <div>{{ $t('agronomySample.summary.sampleType') }}</div>
        <div>{{ $t('netAmount') }}</div>
      </div>

      <div
        v-for="(item, index) in filteredNoAnalyticsRows"
        :key="noAnalyticsKey(item, index)"
        class="table-row offers-no-analytics-row disabled-row"
      >
        <div class="expand-cell">
          <span class="expand-symbol disabled">+</span>
        </div>
        <div>{{ item.Articulo }}</div>
        <div>{{ item.descripcion }}</div>
        <div>{{ item.subFamiliaIdioma }}</div>
        <div>{{ calculateNetAmount(item) }}</div>
      </div>
    </div>

    <div v-else class="offers-no-analytics-empty">
      {{ $t('noDataText') }}
    </div>
  </div>
</template>

<script>
import { DxTextBox } from 'devextreme-vue/text-box';
import { mapGetters } from 'vuex';
import helpers from '@/js/helpers';

export default {
  components: {
    DxTextBox,
  },
  props: {
    store: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      searchText: '',
    };
  },
  computed: {
    ...mapGetters('user', ['getLanguage']),
    ...mapGetters('contact', ['contactInfo']),
    normalizedQuery() {
      return (this.searchText || '').trim().toLowerCase();
    },
    filteredNoAnalyticsRows() {
      const rows = Array.isArray(this.store) ? this.store : [];
      const q = this.normalizedQuery;
      if (!q) return rows;

      return rows.filter((item) =>
        [
          item.Articulo,
          item.descripcion,
          item.subFamiliaIdioma,
          item.PrecioNetoCodDivisa,
          this.calculateNetAmount(item),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(q)
      );
    },
  },
  methods: {
    noAnalyticsKey(item, fallback = '') {
      return [
        item.CodArticulo,
        item.Articulo,
        item.subFamiliaIdioma,
        fallback,
      ]
        .filter((value) => value !== null && value !== undefined)
        .join('-');
    },
    formatAmount(amount, currency) {
      if (amount === null || amount === undefined || amount === '') {
        return '';
      }

      const parsedAmount = Number(
        typeof amount === 'string'
          ? amount.replace(',', '.').replace(/[^\d.-]/g, '')
          : amount
      );

      const locale = this.getLanguage || 'es';
      return helpers.formatCurrency(parsedAmount, currency, locale);
    },
    calculateNetAmount(row) {
      const rawValue = row.PrecioNetoCodDivisa || '';
      const [precioOriginal, moneda] = rawValue.split(' ');
      const precioBruto = Number(
        row.PrecioBruto || precioOriginal || 0
      );
      const dto = Number(row.Descuento || 0);
      let neto = precioBruto;
      if (dto !== 0) {
        neto = precioBruto - (precioBruto * dto) / 100;
      }
      return this.formatAmount(
        neto,
        this.getRowCurrency(row, moneda)
      );
    },
    getRowCurrency(row, moneda) {
      return helpers.resolveCurrency({
        conversionX3: row.ConversionX3,
        clientCurrency: this.contactInfo?.currency,
        additionalCurrencies: [row.CodDivisa],
        m: row.M,
        moneda,
        fallback: 'EUR',
      });
    },
    normalizeCurrency(currency) {
      return helpers.normalizeMonetaryUnit(currency);
    },
  },
};
</script>

<style scoped lang="scss">
.offer-no-analytics-details {
  padding: 0 10px 10px;
}

.offers-no-analytics-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0 0 8px;
}

.offers-no-analytics-header .custom-card-header {
  margin: 0;
  padding: 0 !important;
}

.table-header,
.table-row {
  display: grid;
  grid-template-columns: 35px 1.3fr 1.8fr 1.4fr 1fr;
  padding: 12px;
  background: #f2f8e2;
  border-radius: 10px;
  align-items: center;
  color: #3e3c44;
  margin: 4px 0;
  gap: 8px;
}

.table-header {
  font-weight: 600;
  background-color: #f7f7f8;
}

.table-row {
  border-top: 1px solid #eee;
  color: #00346a;
}

.disabled-row {
  cursor: default;
}

.expand-cell {
  font-weight: 600;
  text-align: center;
}

.expand-symbol {
  display: inline-block;
  font-size: 18px;
  font-weight: 600;
  line-height: 1;
  user-select: none;
}

.expand-symbol.disabled {
  color: #00346a;
}

.offers-no-analytics-empty {
  color: #8a8693;
  padding: 16px 12px;
  text-align: center;
}

.search-row {
  width: 200px;
  margin-left: auto;
}

.search-row :deep(.dx-texteditor) {
  width: 200px;
  height: 35px;
  min-height: 35px;
  border-radius: 999px;
}

.search-row :deep(.dx-texteditor-input) {
  font-size: 13px;
  padding-top: 6px;
  padding-bottom: 6px;
}

.search-row :deep(.dx-texteditor-buttons-container) {
  height: 32px;
}

.search-row :deep(.dx-icon) {
  font-size: 16px;
}

.search-row
  :deep(.dx-texteditor.dx-editor-outlined .dx-texteditor-input) {
  padding-left: 30px !important;
}

.search-row :deep(.dx-texteditor.dx-editor-outlined .dx-placeholder) {
  padding-left: 5px !important;
}
</style>
